<template>
  <div :class="[containerClass]">
    <label v-if="label" :id="`input-${name}-label`" :for="`input${name}`" :class="[labelClass || 'form-label fs-6 fw-bolder text-black', {isRequired: 'required'}]">
      {{ label }} <span v-if="isRequired" class="text-danger"> * </span>
      <a v-if="info" style="padding: 0 !important; margin-left: 5px;" :title="$t(infoText)">
        <i class='fas fa-info-circle' style="font-size: 15px;"></i>
      </a>
    </label>
    <div :class="[IsInputGroup ? 'input-group input-group-solid flex-nowrap' : '']">
      <slot name="leftAddon"/>
      <div :class="{'overflow-hidden flex-grow-1': IsInputGroup}">
        <select :required="isRequired"
                :id="id"
                :key="$i18n.locale"
                class="form-select kt_select2"
                :class="[{ 'invalid': (meta.touched && !meta.valid) }, getClasses(size), selectClass] "
                :name="name"
                v-model="value"
                data-control="select2"
                :data-search="''+showSearch"
                :data-dropdown-parent="parentId ? parentId : ''"
                :data-close-on-select="closeOnSelect"
                :multiple="multiple  ? 'multiple' : false"
                :data-allow-clear="clear || !isRequired"
                :disabled="isDisabled"
                @change="changeSelect"
                :data-placeholder="placeholderText">
          <option></option>
          <slot />
          <template v-if="!$slots.default">
            <option
                v-for="(option, key) in options"
                :key="name+'-option-'+key"
                :value="option.id"
            >
              {{ option.name }}
            </option>
          </template>
        </select>
      </div>
      <span v-show="IsInputGroup" :id="`input-${name}-addon`" @click.prevent="clickAddon" class="input-group-text" :class="clickAddon ? 'cursor-pointer' : ''" style="background-color: #f5f8fa;">
        <span class="svg-icon svg-icon-2 px-1 py-3 text-center"
              :style="{
          border: `1px solid ${$getCssVariable(iconColor)}`,
          borderRadius: '5px',
          backgroundColor: '#fff',
          width: '30px !important',
          height: '30px !important'
        }">
            <i :class="icon" :style="{ color: $getCssVariable(iconColor) }"></i>
        </span>
      </span>
    </div>
    <span class="text-danger" v-show="errorMessage"> {{ errorMessage }} </span>
  </div>
</template>


<script>

import {nextTick} from "vue";
import {useField} from "vee-validate";

export default {
  name: 'Select2',
  data() {
    const { handleChange, value, handleBlur, errorMessage, meta } = useField(() => this.name, undefined, {
      syncVModel: true,
    })
    return {
      handleChange,
      value,
      handleBlur,
      errorMessage,
      meta,
      syncingValue: false,
    }
  },
  props: {
    name: { type: String, required: true },
    parentId: { type: String, default: '' },
    placeholderText: { type: String, default: '' },
    showSearch: { type: Boolean, default: true },
    multiple: { type: Boolean, required: true },
    rules: String,
    labelClass: {type: String, default: "form-label"},
    selectClass: {type: String, default: "form-select form-control-lg form-select-solid"},
    containerClass: {
      type: String,
      default: 'col-12 mt-2 mb-5'
    },
    label: String,
    isDisabled: Boolean,
    isRequired: {type:Boolean, default: true},
    modelValue: [String, Array],
    iconText: String,
    IsInputGroup: Boolean,
    icon: {type: String, default: 'fas fa-plus'},
    iconDir: String,
    iconColor: {type: String, default: '--bs-primary'},
    addonClass: String,
    clickAddon: Function,
    info: Boolean,
    infoText: String,
    size: { type: String, default: 'lg' },
    clear: { type: Boolean, default: false },
    closeOnSelect: { type: Boolean, default: true },
    options: {type: Array, default: () => []},
  },
  computed: {
    id() {
      return `select-${this.name}-id`
    },
  },
  watch: {
    modelValue : function(value){
      if(value) {
        nextTick(() => {
          this.initSelect2()
        })
      }
    },
    id : function() {
      nextTick(() => {
        this.initSelect2()
      })
    },
    '$i18n.locale' : function(value) {
      if(value) {
        this.$nextTick(() => {
          this.initSelect2()
        })
      }
    },

  },
  mounted() {
    this.initSelect2()
  },
  updated() {
    this.$nextTick(() => {
      this.initSelect2()
    })
  },
  methods: {
    async initSelect2() {
      KTApp.initSelect2()
      const select =  $("#"+this.id)
      const opts = {
        minimumResultsForSearch: this.showSearch ? 2 : Infinity,
        closeOnSelect: this.closeOnSelect,
        allowClear: this.clear,
      }
      if (this.parentId) {
        opts.dropdownParent = $(this.parentId)
      }
      select.select2(opts)

      // Use a namespaced event to prevent handler accumulation across re-inits.
      // Each call to initSelect2 replaces the previous handler instead of adding another.
      select.off('change.vue')

      if(this.multiple) {
        select.on('change.vue', (e) => {
          if (this.syncingValue) return
          let result = [];
          if(e.target.value) {
            for (var i = 0, iLen = e.target.selectedOptions.length; i < iLen; i++) {
              if (e.target.selectedOptions[i].value != '' || e.target.selectedOptions[i].value != undefined) {
                result.push(e.target.selectedOptions[i].value);
              }
            }
          }
          this.$emit('update:modelValue', result);
        });
      } else {
        select.on('change.vue', (e) => {
          if (this.syncingValue) return
          this.$emit('update:modelValue', e.target.value);
        });
      }

      // Sync the currently bound value into Select2's display after (re)init.
      // trigger('change') updates Select2's visual state. The syncingValue flag
      // prevents our own handler from re-emitting (avoiding circular updates).
      const currentValue = this.value
      if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
        this.syncingValue = true
        select.val(currentValue).trigger('change')
        this.syncingValue = false
      }
    },
    getIcon (icon) {
      return icon ? icon : null
    },
    hasIcon (icon) {
      return icon ? "input-group" : null
    },
    getClasses (size) {
      let sizeValue, isValidValue;

      sizeValue = size ? `form-control-${size}` : null;

      isValidValue = this.meta.touched && !this.meta.valid ? `invalid` : "valid";

      return `${sizeValue} ${isValidValue}`;
    },
    changeSelecchangeSelectt(select){
      this.$emit('change',select);
    }
  }

}
</script>
<style scoped>
.select2-container--bootstrap5 .select2-selection--multiple:not(.form-select-sm):not(.form-select-lg) {
  padding-top: calc((2.5rem + 2px - 0.8rem)/ 2);
  padding-bottom: calc((2.5rem + 2px - 0.8rem)/ 2);
}
.select2-container .select2-selection--multiple, .kt_select2{
  min-height: 44.89px !important;
}

.select-container {
  position: relative;
}

.input-group-text {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.5;
  color: var(--bs-secondary);
  text-align: center;
  white-space: nowrap;
  background-color: #f5f8fa;
  border: 1px solid #e4e6ef;
  border-radius: 0.475rem;
}
</style>
